import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/utils';
import { User, Lock, Phone, Envelope, HardHat, House, Buildings, CaretLeft, CaretRight, Check } from '@phosphor-icons/react';

const ROLES = [
  { id: 'customer', label: 'I need an artisan', desc: 'Post jobs, receive bids, pay safely via escrow', icon: House, color: '#1A4D2E' },
  { id: 'artisan', label: "I'm an artisan", desc: 'Browse jobs in your specialty, place bids', icon: HardHat, color: '#E86A33' },
  { id: 'facility_manager', label: "I'm a Facility Manager", desc: 'Bid on all jobs, quality assurance focused', icon: Buildings, color: '#2D6A4F' },
];

const TRADES = [
  { id: 'plumbing', name: 'Plumbing' }, { id: 'electrical', name: 'Electrical' },
  { id: 'ac_cooling', name: 'AC & Cooling' }, { id: 'carpentry', name: 'Carpentry' },
  { id: 'painting', name: 'Painting' }, { id: 'tiling', name: 'Tiling' },
  { id: 'welding', name: 'Welding' }, { id: 'solar_power', name: 'Solar & Power' },
  { id: 'auto_services', name: 'Auto Services' }, { id: 'cleaning', name: 'Cleaning' },
  { id: 'interior', name: 'Interior' }, { id: 'masonry', name: 'Masonry' },
];

const STATES = ['Lagos', 'Abuja (FCT)', 'Rivers', 'Kano', 'Ogun', 'Oyo', 'Anambra', 'Delta', 'Enugu', 'Edo', 'Imo', 'Kaduna'];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', state: '', lga: '', trade: '', nin: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lgas, setLgas] = useState([]);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadLgas = async (state) => {
    set('state', state);
    set('lga', '');
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const { data } = await (await import('axios')).default.get(`${API}/locations`);
      setLgas(data.locations[state] || []);
    } catch { setLgas([]); }
  };

  const canNext = () => {
    if (step === 1) return !!role;
    if (step === 2) return form.name.length > 1 && form.phone.length >= 10 && form.password.length >= 6;
    if (step === 3) {
      if (role === 'customer') return !!form.state && !!form.lga;
      return !!form.trade && !!form.state && !!form.lga;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      if (user.role === 'artisan' || user.role === 'facility_manager') navigate('/feed');
      else navigate('/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    if (step === 3) { handleSubmit(); return; }
    setStep(s => s + 1);
  };

  const totalSteps = 3;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-[460px]">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-3xl font-black text-[#1A4D2E]">Naija<span className="text-[#E86A33]">Fix</span></h1>
          </Link>
          <p className="text-[#5C5C5C] mt-1 font-body text-sm">Step {step} of {totalSteps}</p>
          <div className="w-full bg-[#E5E3DB] h-1 rounded-full mt-3">
            <div className="bg-[#1A4D2E] h-1 rounded-full transition-all duration-300" style={{ width: `${(step / totalSteps) * 100}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#D4C5B3] p-6 shadow-sm">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="register-error">{error}</div>}

          {step === 1 && (
            <div>
              <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Choose your role</h2>
              <p className="text-sm text-[#5C5C5C] mb-5 font-body">How will you use NaijaFix?</p>
              <div className="space-y-3">
                {ROLES.map(r => (
                  <button
                    key={r.id}
                    data-testid={`role-${r.id}`}
                    onClick={() => setRole(r.id)}
                    className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${role === r.id ? 'border-[#1A4D2E] bg-[#1A4D2E]/5' : 'border-[#D4C5B3] hover:border-[#1A4D2E]/30'}`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: r.color + '15' }}>
                      <r.icon size={24} weight="duotone" color={r.color} />
                    </div>
                    <div>
                      <div className="font-bold text-[#1C1C1C] font-body">{r.label}</div>
                      <div className="text-xs text-[#5C5C5C] font-body">{r.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Your details</h2>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={20} />
                  <input data-testid="register-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ade Balogun" className="w-full bg-white border border-[#D4C5B3] rounded-lg pl-10 pr-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none font-body" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={20} />
                  <input data-testid="register-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="08012345678" className="w-full bg-white border border-[#D4C5B3] rounded-lg pl-10 pr-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none font-body" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Email (Optional)</label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={20} />
                  <input data-testid="register-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="ade@email.com" className="w-full bg-white border border-[#D4C5B3] rounded-lg pl-10 pr-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none font-body" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={20} />
                  <input data-testid="register-password" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 6 characters" className="w-full bg-white border border-[#D4C5B3] rounded-lg pl-10 pr-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none font-body" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">{role === 'customer' ? 'Your location' : 'Trade & Location'}</h2>
              {role !== 'customer' && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Your Trade / Specialty</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TRADES.map(t => (
                      <button key={t.id} data-testid={`trade-${t.id}`} onClick={() => set('trade', t.id)} className={`p-3 rounded-lg border text-left text-sm font-body font-semibold transition-all ${form.trade === t.id ? 'border-[#E86A33] bg-[#E86A33]/5 text-[#E86A33]' : 'border-[#D4C5B3] text-[#5C5C5C] hover:border-[#E86A33]/40'}`}>
                        {t.name}
                      </button>
                    ))}
                  </div>
                  {role === 'facility_manager' && <p className="text-xs text-[#2D6A4F] mt-2 font-body">As a Facility Manager, you'll see jobs across all categories.</p>}
                </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">State</label>
                <select data-testid="register-state" value={form.state} onChange={e => loadLgas(e.target.value)} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 outline-none font-body">
                  <option value="">Select State</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">LGA</label>
                <select data-testid="register-lga" value={form.lga} onChange={e => set('lga', e.target.value)} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 outline-none font-body">
                  <option value="">Select LGA</option>
                  {lgas.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 4 && null}

          <div className="flex gap-3 mt-6">
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 px-4 py-3 bg-[#E8DDD2] text-[#5C5C5C] font-bold rounded-lg font-body" data-testid="register-back">
                <CaretLeft size={16} /> Back
              </button>
            )}
            <button
              data-testid="register-next-button"
              onClick={next}
              disabled={!canNext() || loading}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#133922] text-white font-bold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2 font-body"
            >
              {loading ? 'Creating account...' : step === 3 ? 'Create Account' : 'Continue'}
              {!loading && step < 3 && <CaretRight size={16} />}
            </button>
          </div>
          <div className="mt-4 text-center">
            <p className="text-sm text-[#5C5C5C] font-body">Already have an account? <Link to="/login" className="text-[#E86A33] font-bold hover:underline" data-testid="go-to-login">Sign in</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
